import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/shared/SEOHead';
import { AdfSidebar, AdfSchemaViewer, AdfSpecDownloadDialog } from '@/components/adf-standard';
import { useAdfElementTree, findElementBySlug } from '@/hooks/useAdfStandard';
import { useAnalytics } from '@/hooks/useAnalytics';
import { FileCode, Menu, Hammer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
function SidebarSkeleton() {
  return (
    <div className="w-64 shrink-0 space-y-4">
      <Skeleton className="h-5 w-32" />
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="space-y-1">
            <Skeleton className="h-7 w-full" />
            <div className="ml-4 space-y-1">
              <Skeleton className="h-6 w-5/6" />
              <Skeleton className="h-6 w-4/6" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ViewerSkeleton() {
  return (
    <div className="flex-1 min-w-0 space-y-6">
      {/* Header */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-6 w-20 rounded-full" />
        </div>
        <Skeleton className="h-4 w-full max-w-xl" />
      </div>
      {/* Tabs */}
      <Skeleton className="h-10 w-72" />
      {/* Content */}
      <div className="space-y-4">
        <Skeleton className="h-6 w-32" />
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdfStandard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: elementTree, isLoading } = useAdfElementTree();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { trackAdfElementView } = useAnalytics();

  // Get active element from URL or default to 'adf'
  const activeSlug = searchParams.get('element') || 'adf';

  // Find active element in tree
  const activeElement = elementTree ? findElementBySlug(elementTree, activeSlug) : null;

  // Track element view when active element changes
  useEffect(() => {
    if (activeSlug && !isLoading) {
      trackAdfElementView(activeSlug);
    }
  }, [activeSlug, isLoading, trackAdfElementView]);

  // Set default element when data loads
  useEffect(() => {
    if (elementTree && elementTree.length > 0 && !searchParams.get('element')) {
      setSearchParams({ element: elementTree[0].slug }, { replace: true });
    }
  }, [elementTree, searchParams, setSearchParams]);

  const handleElementSelect = (slug: string) => {
    setSearchParams({ element: slug });
    setSidebarOpen(false); // Close mobile sidebar
  };

  return (
    <Layout>
      <SEOHead 
        path="/adf-standard"
        title="ADF v2.0 Standard - Auto-lead Data Format Specification"
        description="Browse the ADF v2.0 (Auto-lead Data Format) XML specification. Includes chat history support, SMS opt-out, lead type classification, and comprehensive validation."
      />
      
      <div className="container py-4 md:py-8">
        {/* Page Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-2">
            <div className="flex items-center gap-3">
              <FileCode className="h-6 w-6 md:h-8 md:w-8 text-primary" />
              <h1 className="text-2xl md:text-3xl font-bold">ADF Standard</h1>
              <Badge variant="secondary" className="text-xs font-medium">v2.0</Badge>
            </div>
            <div className="flex gap-2">
              <Button variant="default" size="sm" asChild>
                <Link to="/adf-standard/builder">
                  <Hammer className="h-4 w-4 mr-2" />
                  Builder
                </Link>
              </Button>
              <AdfSpecDownloadDialog />
            </div>
          </div>
          <p className="text-muted-foreground text-base md:text-lg max-w-3xl">
            The Auto-lead Data Format (ADF) v2.0 is the automotive industry standard for exchanging 
            lead information between dealers, OEMs, and third-party providers. Now with chat history, 
            lead type classification, and SMS opt-out support.
          </p>
        </div>

        {/* Content */}
        <div className="flex gap-4 lg:gap-8">
          {/* Desktop sidebar */}
          <div className="hidden lg:block">
            {isLoading ? (
              <SidebarSkeleton />
            ) : elementTree ? (
              <AdfSidebar
                elementTree={elementTree}
                activeSlug={activeSlug}
                onElementSelect={handleElementSelect}
              />
            ) : null}
          </div>

          {/* Main content */}
          <main className="flex-1 min-w-0">
            {isLoading ? (
              <ViewerSkeleton />
            ) : activeElement && elementTree ? (
              <AdfSchemaViewer
                element={activeElement}
                elementTree={elementTree}
                onElementSelect={handleElementSelect}
              />
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                Select an element from the sidebar to view its structure.
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Mobile floating nav */}
      <div className="lg:hidden fixed bottom-4 right-4 z-50">
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetTrigger asChild>
            <Button size="icon" className="rounded-full shadow-lg h-12 w-12">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle element tree</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 pt-12">
            {isLoading ? (
              <SidebarSkeleton />
            ) : elementTree ? (
              <AdfSidebar
                elementTree={elementTree}
                activeSlug={activeSlug}
                onElementSelect={handleElementSelect}
                isMobile
              />
            ) : null}
          </SheetContent>
        </Sheet>
      </div>
    </Layout>
  );
}
